import React, { useEffect, useRef, useState } from 'react';
import hark from 'hark';
import './VoiceCall.css';

const SILENCE_THRESHOLD = -50;
const SILENCE_DURATION = 900;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const VoiceCall = ({ sessionId, model = 'KHUSUS', onBack }) => {
  const [status, setStatus] = useState('idle');
  const [logs, setLogs] = useState([]);

  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);
  const streamRef = useRef(null);
  const speechEvents = useRef(null);
  const silenceTimer = useRef(null);
  const statusRef = useRef('idle');
  const isSessionActiveRef = useRef(false);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => () => stopEverything(), []);

  const stopEverything = () => {
    isSessionActiveRef.current = false;
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }

    if (speechEvents.current) {
      speechEvents.current.stop();
      speechEvents.current = null;
    }

    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setStatus('idle');
  };

  const startCall = async () => {
    if (!sessionId) {
      setLogs((prev) => ['Error: sessionId tidak ditemukan.', ...prev]);
      return;
    }

    try {
      isSessionActiveRef.current = true;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      setupVAD(stream);
      setupRecorder(stream);

      if (mediaRecorder.current && mediaRecorder.current.state === 'inactive') {
        mediaRecorder.current.start();
      }

      setStatus('listening');
    } catch (err) {
      isSessionActiveRef.current = false;
      setLogs((prev) => [`Error: ${err.message}`, ...prev]);
    }
  };

  const setupVAD = (stream) => {
    if (speechEvents.current) speechEvents.current.stop();

    speechEvents.current = hark(stream, {
      threshold: SILENCE_THRESHOLD,
      interval: 100,
    });

    speechEvents.current.on('speaking', () => {
      if (statusRef.current === 'processing' || statusRef.current === 'speaking') return;
      if (silenceTimer.current) clearTimeout(silenceTimer.current);
      setStatus('listening');
    });

    speechEvents.current.on('stopped_speaking', () => {
      if (statusRef.current === 'processing' || statusRef.current === 'speaking') return;
      if (silenceTimer.current) clearTimeout(silenceTimer.current);

      silenceTimer.current = setTimeout(() => {
        if (statusRef.current === 'listening') {
          stopRecordingAndSend();
        }
      }, SILENCE_DURATION);
    });
  };

  const setupRecorder = (stream) => {
    mediaRecorder.current = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 32000,
    });

    mediaRecorder.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.current.push(event.data);
      }
    };

    mediaRecorder.current.onstop = async () => {
      const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
      audioChunks.current = [];

      if (audioBlob.size > 3000) {
        await sendAudioToBackend(audioBlob);
      } else {
        restartListening();
      }
    };
  };

  const stopRecordingAndSend = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      setStatus('processing');
      mediaRecorder.current.stop();
    }
  };

  const sendAudioToBackend = async (blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'voice_input.webm');
    formData.append('sessionId', sessionId);
    formData.append('model', model);

    try {
      const response = await fetch(`${API_BASE_URL}/chat/voice`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || 'Gagal memproses voice chat.');
      }

      setLogs((prev) => [
        `You: ${data.question || '(voice input)'}`,
        `ChatLaw: ${String(data.answer || '').substring(0, 70)}...`,
        ...prev,
      ]);

      if (data.audio) {
        playAudio(data.audio);
      } else if (data.audioPath) {
        const cleanPath = String(data.audioPath).replace(/^\/+/, '');
        playAudio(`${API_BASE_URL}/${cleanPath}`);
      } else {
        restartListening();
      }
    } catch (error) {
      setLogs((prev) => [`Error: ${error.message}`, ...prev]);
      restartListening();
    }
  };

  const playAudio = (audioSource) => {
    setStatus('speaking');

    if (speechEvents.current) speechEvents.current.suspend();

    const audio = new Audio(audioSource);
    audio.play().catch(() => {
      if (isSessionActiveRef.current) restartListening();
    });

    audio.onended = () => {
      if (!isSessionActiveRef.current) return;
      if (speechEvents.current) speechEvents.current.resume();
      restartListening();
    };
  };

  const restartListening = () => {
    if (!isSessionActiveRef.current) return;

    setStatus('listening');

    const recorder = mediaRecorder.current;
    const stream = streamRef.current;
    const streamIsLive = Boolean(stream && stream.getTracks().some((track) => track.readyState === 'live'));

    if (recorder && recorder.state === 'inactive' && streamIsLive) {
      try {
        recorder.start();
      } catch (error) {
        console.warn('Failed to restart MediaRecorder:', error);
      }
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'listening':
        return '🎙️';
      case 'processing':
        return '🧠';
      case 'speaking':
        return '🗣️';
      default:
        return '🎤';
    }
  };

  const getMainText = () => {
    switch (status) {
      case 'listening':
        return 'Mendengarkan...';
      case 'processing':
        return 'ChatLaw sedang memproses...';
      case 'speaking':
        return 'ChatLaw menjawab...';
      default:
        return 'Voice Chat Hukum';
    }
  };

  const getSubText = () => {
    switch (status) {
      case 'listening':
        return 'Silakan bicara tentang hukum';
      case 'processing':
        return 'Menganalisis konteks knowledge';
      case 'speaking':
        return 'Dengarkan jawaban ChatLaw';
      default:
        return 'Klik mulai untuk membuka mikrofon';
    }
  };

  return (
    <div className={`voice-container ${status}`}>
      {typeof onBack === 'function' ? (
        <button type="button" className="voice-back-btn" onClick={onBack}>
          Kembali ke Chat
        </button>
      ) : null}

      <div className="avatar-wrapper">
        <div className="loading-ring"></div>
        <div className="ripple"></div>
        <div className="avatar-circle">{getIcon()}</div>
      </div>

      <div className="status-text">{getMainText()}</div>
      <div className="sub-status">{getSubText()}</div>

      {status === 'idle' ? (
        <button className="start-btn" onClick={startCall}>
          Mulai Voice Chat
        </button>
      ) : (
        <button className="start-btn stop-btn" onClick={stopEverything}>
          Akhiri Voice Chat
        </button>
      )}

      <div className="chat-log">
        {logs.map((log, idx) => (
          <div key={idx}>{log}</div>
        ))}
      </div>
    </div>
  );
};

export default VoiceCall;
